import * as React from 'react';
import Image from 'next/image';
import { getAssetSrc, useResponsiveBreakPoints, cn } from '@restworld/utils-common';
import { RATING_RANGE } from '@restworld/utility-types';

import { FlowbiteStarSizes, Rating } from '../rating';
import { Heading } from '@restworld/ui-ds-base';

export type GoogleRatingCardProps = {
  rating: number;
  description?: string;
  fullWidth?: boolean;
  googleLogoSize?: {
    height: number;
    width: number;
  };
  backgroundColor?: 'gray' | 'white';
  wrapperStyle?: string;
  ratingIconSize?: keyof FlowbiteStarSizes;
  ratingLabelSize?: 'sm' | 'md' | 'xs';
  showGoogleIcon?: boolean;
  className?: string;
};

export const GoogleRatingCard = ({
  rating,
  description,
  fullWidth,
  googleLogoSize,
  backgroundColor,
  wrapperStyle,
  className,
  ratingIconSize,
  ratingLabelSize,
  showGoogleIcon = true,
}: GoogleRatingCardProps) => {
  const { isLargeDesktop } = useResponsiveBreakPoints();
  return (
    <CardWrapper
      backgroundColor={backgroundColor}
      wrapperStyle={wrapperStyle}
      fullWidth={fullWidth}
      className={className}
    >
      {showGoogleIcon ? (
        <Image
          {...{
            src: getAssetSrc('/utils-assets/google-icon.svg'),
            width: isLargeDesktop ? 110 : 71,
            height: isLargeDesktop ? 40 : 24,
            alt: 'google',
            // Keep aspect ratio when surrounding CSS constrains one dimension (silences Next's
            // "width or height modified, but not the other" warning).
            style: { height: 'auto' },
            ...googleLogoSize,
          }}
        />
      ) : null}
      <div className="flex flex-row gap-2 items-center">
        <Label ratingLabelSize={ratingLabelSize} htmlFor="rating">
          {rating}
        </Label>
        <div id="rating" className="flex flex-col gap-1">
          <Rating rating={+rating as RATING_RANGE} size={ratingIconSize ?? isLargeDesktop ? 'md' : 'sm'} />
          <Heading as="span" size={6} weight="normal" color="muted" className="text-xs">
            {description}
          </Heading>
        </div>
      </div>
    </CardWrapper>
  );
};

const CardWrapper = ({ fullWidth, backgroundColor, wrapperStyle, className, ...props }: any) => (
  <div
    className={cn(
      'flex flex-row items-center justify-center gap-6 w-fit mx-auto p-6 rounded-lg',
      backgroundColor === 'white' ? 'bg-white' : 'bg-gray-100',
      wrapperStyle,
      fullWidth ? 'w-full' : '',
      className
    )}
    {...props}
  />
);

const Label = ({ ratingLabelSize, className, ...props }: any) => (
  <label
    className={cn(
      ratingLabelSize === 'md'
        ? 'text-2xl md:text-4xl md:leading-[3.375rem] leading-9'
        : ratingLabelSize === 'xs'
        ? 'text-base leading-7'
        : 'text-xl leading-8',
      'font-semibold text-gray-900',
      className
    )}
    {...props}
  />
);
